import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { inr, fmtDateTime } from "@/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus } from "lucide-react";

export default function Orders() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ customer_name: "", phone: "", items: "", total: "" });

  const load = useCallback(() => api.get("/orders").then((r) => setItems(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    try {
      await api.post("/orders", { ...form, total: parseFloat(form.total) || 0 });
      toast.success("Order created");
      setOpen(false); setForm({ customer_name: "", phone: "", items: "", total: "" }); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-5" data-testid="orders-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight">Sales Orders</h1>
          <p className="text-sm text-muted-foreground">Order book for your product business</p>
        </div>
        <Button data-testid="add-order-button" onClick={() => setOpen(true)} className="bg-primary hover:bg-primary-hover"><Plus size={16} className="mr-2" /> New Order</Button>
      </div>

      <Card className="overflow-hidden">
        <table className="w-full text-sm" data-testid="orders-table">
          <thead className="bg-muted/60">
            <tr className="text-left text-xs uppercase tracking-widest text-muted-foreground">
              <th className="px-4 py-3">Order No</th><th className="px-4 py-3">Customer</th><th className="px-4 py-3">Items</th>
              <th className="px-4 py-3 text-right">Total</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Created</th>
            </tr>
          </thead>
          <tbody>
            {items.map((o) => (
              <tr key={o.id} data-testid={`order-row-${o.id}`} className="border-t hover:bg-accent/40 transition-colors">
                <td className="px-4 py-3 font-mono text-xs">{o.order_no}</td>
                <td className="px-4 py-3"><div className="font-medium">{o.customer_name}</div><div className="text-xs text-muted-foreground">{o.phone}</div></td>
                <td className="px-4 py-3 text-muted-foreground max-w-[280px] truncate">{o.items}</td>
                <td className="px-4 py-3 text-right font-mono">{inr(o.total)}</td>
                <td className="px-4 py-3"><span className="text-xs px-2 py-1 rounded-full border bg-primary text-primary border-primary">{o.status}</span></td>
                <td className="px-4 py-3 text-xs text-muted-foreground">{fmtDateTime(o.created_at)}</td>
              </tr>
            ))}
            {!items.length && <tr><td colSpan={6} className="px-4 py-10 text-center text-muted-foreground">No orders yet</td></tr>}
          </tbody>
        </table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="add-order-dialog">
          <DialogHeader><DialogTitle className="font-heading">New Sales Order</DialogTitle></DialogHeader>
          <form onSubmit={add} className="space-y-3">
            <Input data-testid="order-customer-input" placeholder="Customer name" value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} required />
            <Input data-testid="order-phone-input" placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            <Input data-testid="order-items-input" placeholder="Items, e.g. Rice 25kg x2" value={form.items} onChange={(e) => setForm({ ...form, items: e.target.value })} />
            <Input data-testid="order-total-input" type="number" step="0.01" placeholder="Total ₹" value={form.total} onChange={(e) => setForm({ ...form, total: e.target.value })} required />
            <Button data-testid="order-save-button" type="submit" className="w-full bg-primary hover:bg-primary-hover">Create Order</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
